// InputComponent.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Loading, { LoadingType } from '../component/Loading.jsx';

import { NULL_FUNCTION, EMPTY_OBJECT } from '../resource/defaults.js';

import { color } from '../style/variables.js';
import ResetInputStyle from '../style/ResetInputStyle.js';

export const Theme = {
  DARK: 'dark',
  LIGHT: 'light',
};

const ComponentColor = {
  [Theme.DARK]: {
    Label: {
      color: 'rgba(221, 221, 221, 0.8)',
    },
    InputWrapper: {
      borderColor: 'rgba(71, 71, 71, 0.4)',
      backgroundColor: color.neutral[10],
    },
    Input: {
      color: color.neutral[100],
    },
  },
  [Theme.LIGHT]: {
    Label: {
      color: color.neutral[10],
      placeholderColor: 'rgba(221, 221, 221, 0.4)',
    },
    InputWrapper: {
      borderColor: color.neutral[95],
    },
    backgroundColor: color.neutral[95],
  },
  Input: {
    color: color.neutral[30],
    placeholderColor: color.neutral[75],
  },
};

export const InputComponent = ({
  theme = Theme.LIGHT,
  value = '',
  inputType = 'text',
  inputAutocomplete = '',
  inputName = '',
  placeholderI18nKey = '',
  placeholderI18nOption = EMPTY_OBJECT,
  labelI18nKey = '',
  hintI18nKey = '',
  errorI18nKey = '',
  labelI18nOption = EMPTY_OBJECT,
  hintI18nOption = EMPTY_OBJECT,
  errorI18nOption = EMPTY_OBJECT,
  isControlledComponent = true,
  isEnabled = false,
  isLoading = false,
  isError = false,
  shouldShowHint = false,
  labelComponent = null,
  leadingComponent = null,
  trailingComponent = null,
  hintComponent = null,
  onChange = NULL_FUNCTION,
  onFocus = NULL_FUNCTION,
  onBlur = NULL_FUNCTION,
  getElementRef = NULL_FUNCTION,
}) => {
  const { t } = useTranslation();
  const themeColor = ComponentColor[theme];
  return (
    <StyledInputComponent>
      {labelComponent ? (
        labelComponent
      ) : labelI18nKey ? (
        <Label {...themeColor.Label}>{t(labelI18nKey, labelI18nOption)}</Label>
      ) : null}
      <InputWrapper isError={isError} {...themeColor.InputWrapper}>
        {leadingComponent}
        <Input
          {...themeColor.Input}
          ref={getElementRef}
          type={inputType}
          // https://www.chromium.org/developers/design-documents/form-styles-that-chromium-understands/
          autoComplete={inputAutocomplete}
          name={inputName}
          placeholder={t(placeholderI18nKey, placeholderI18nOption)}
          disabled={!isEnabled}
          {...(isControlledComponent ? { value } : {})}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
        />
        {isLoading ? (
          <LoadingWrapper>
            <Loading size={24} type={LoadingType.DARK} />
          </LoadingWrapper>
        ) : (
          trailingComponent
        )}
      </InputWrapper>
      {isError ? (
        errorI18nKey ? (
          <ErrorHint>{t(errorI18nKey, errorI18nOption)}</ErrorHint>
        ) : null
      ) : shouldShowHint ? (
        hintComponent ? (
          hintComponent
        ) : (
          <Hint>{t(hintI18nKey, hintI18nOption)}</Hint>
        )
      ) : null}
    </StyledInputComponent>
  );
};

InputComponent.propTypes = {
  theme: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  inputType: PropTypes.string,
  inputAutocomplete: PropTypes.string,
  inputName: PropTypes.string,
  placeholderI18nKey: PropTypes.string,
  labelI18nKey: PropTypes.string,
  hintI18nKey: PropTypes.string,
  errorI18nKey: PropTypes.string,
  placeholderI18nOption: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
  ]),
  labelI18nOption: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  hintI18nOption: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  errorI18nOption: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  isControlledComponent: PropTypes.bool,
  isEnabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  shouldShowHint: PropTypes.bool,
  labelComponent: PropTypes.node,
  leadingComponent: PropTypes.node,
  trailingComponent: PropTypes.node,
  hintComponent: PropTypes.node,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  getElementRef: PropTypes.func,
};

const StyledInputComponent = styled.div``;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 48px;
  margin-bottom: 4px;
  padding: 12px;
  border-width: 1px;
  border-style: solid;
  border-color: ${({ isError, borderColor }) =>
    isError ? color.red : borderColor};
  border-radius: 4px;
  background-color: ${({ backgroundColor }) => backgroundColor};
`;

export const Label = styled.label`
  margin-bottom: 12px;
  display: block;
  font-size: 12px;
  line-height: 1.4;
  color: ${({ color }) => color};
`;

const Input = styled.input`
  ${ResetInputStyle};
  padding-top: 12px;
  padding-bottom: 12px;
  width: 100%;
  height: 100%;
  flex: auto;
  font-size: 16px;
  color: ${({ color }) => color};
  ::placeholder {
    color: ${({ placeholderColor }) => placeholderColor};
  }

  /* Remove autocomplete background styles */
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px ${color.neutral[100]} inset !important;
    -webkit-text-fill-color: ${color.neutral[0]} !important;
  }
`;

const LoadingWrapper = styled.div`
  flex: none;
`;

export const Hint = styled.div`
  font-size: 12px;
  line-height: 1.4;
  color: ${({ isError }) => (isError ? color.red : color.neutral[75])};
`;

export const ErrorHint = styled(Hint)`
  font-size: 12px;
  line-height: 1.4;
  color: ${color.red};
`;

export default React.forwardRef((props, ref) => (
  <InputComponent getElementRef={ref} {...props} />
));
